import React, { useState, useEffect } from 'react';
import { Typography, Grid, Button, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Availability from './Availability';
import Frequency from './Frequency';
import {
    CourseFrequencyType, CourseFrequencyPayload, CourseAvailabilityType, CourseAvailabilityPayload
} from '@beable/roster-backend-client-v1';
import { getStyles } from '../styles';
import { useCourseState, useCourseDispatch, CourseActionType, ProvisionCourseStep } from '../../../context/CourseContext';
import { isEmpty, isEqual } from 'lodash';
import courseService, { DeliveryRules } from '../../../services/CourseService'
import Language from './Language';
import { Languages } from '@beable/content-types';
import SkipDates from './SkipDates';

export default function Delivery() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { site, course, courseSet, schoolId, deliverySelections, currentAcademicYears } = useCourseState();
    const courseDispatch = useCourseDispatch();
    const [savedRules, setSavedRules] = useState<DeliveryRules>();
    const [preselected, setPreselected] = useState<DeliveryRules>();
    
    const [frequencyType, setFrequencyType] = useState('' as CourseFrequencyType);
    const [frequencyPayload, setFrequencyPayload] = useState({} as CourseFrequencyPayload);
    const [availabilityType, setAvailabilityType] = useState('' as CourseAvailabilityType);
    const [availabilityPayload, setAvailabilityPayload] = useState({} as CourseAvailabilityPayload);
    const [languageType, setLanguageType] = useState<Languages | undefined>(undefined);
    const [ignoreSkipDates, setIgnoreSkipDates] = useState<boolean>(!!deliverySelections?.ignoreSkipDates);

    useEffect(() => {
      const getCourseDeliveryRules = async () => {
          const orgId = (schoolId === 'ALL' ? undefined : schoolId)
          const data = await courseService.getCourseDeliveryRules(site?.id as number, course?.courseId, courseSet?.id, orgId);

          if (data) {
            const rules: DeliveryRules = {
              frequencyType: data.frequencyType,
              frequencyPayload: data.frequencyPayload,
              availabilityType: data.availabilityType,
              availabilityPayload: data.availabilityPayload,
              oldStart: data.oldStart,
              languageType: data.languageCode,
              ignoreSkipDates: data.ignoreSkipDates,
            }
            setSavedRules(rules);
          } else {
            setSavedRules(undefined);
          }
      };

      getCourseDeliveryRules();
    }, [site, course, courseSet, schoolId]);

    useEffect(() => {
      if (deliverySelections) {
        setPreselected(deliverySelections);
        setIgnoreSkipDates(!!deliverySelections.ignoreSkipDates);
      } else {
        setPreselected(savedRules);
        setIgnoreSkipDates(!!savedRules?.ignoreSkipDates);
      }
    }, [savedRules, course, deliverySelections]);

    const isValid = (): boolean => {
      if ( isEmpty(frequencyType)
        || isEmpty(frequencyPayload)
        || isEmpty(availabilityType)
        || (isEmpty(availabilityPayload))
      ) {
        return false;
      }
      return true;
    }

    const handleLanguageTypeChange = (newLanguage: string) => {
      const selectedLanguage = newLanguage ? Object.values(Languages).find(
        (type) => type === newLanguage as Languages
      ) : undefined;
      setLanguageType(selectedLanguage);
    };

    const handleIgnoreSkipDatesChange = (skipDates: string) => {
      setIgnoreSkipDates(skipDates === 'disregard');
    }
    
    const isDeliveryChanged = (): boolean => {
      if ( savedRules?.frequencyType === frequencyType
        && isEqual(savedRules?.frequencyPayload, frequencyPayload)
        && savedRules?.availabilityType === availabilityType
        && isEqual(savedRules?.availabilityPayload, availabilityPayload)
        && savedRules?.languageType == languageType
        && savedRules?.ignoreSkipDates === ignoreSkipDates
      ) {
        return false;
      }
      return true;
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant='h6' sx={styles.stepContainer_header}>
          Course Delivery
        </Typography>
        <Box sx={styles.stepContainer_section}>
          <Availability
            availabilityType={preselected?.availabilityType}
            availabilityPayload={preselected?.availabilityPayload}
            handleTypeChange={setAvailabilityType}
            handlePayloadChange={setAvailabilityPayload}
            currentAcademicYears={currentAcademicYears}
            oldStart={preselected?.oldStart}
          />
        </Box>
        <Box sx={styles.stepContainer_section}>
          <SkipDates 
            ignoreSkipDates={ignoreSkipDates} 
            handleIgnoreSkipDatesChange={handleIgnoreSkipDatesChange}
          />
        </Box>
        <Box sx={styles.stepContainer_section}>
          <Frequency
            frequencyType={preselected?.frequencyType}
            frequencyPayload={preselected?.frequencyPayload}
            handleTypeChange={setFrequencyType}
            handlePayloadChange={setFrequencyPayload}
            lessonCount={(course?.lessons && course?.lessons.length) || 1000}
          />
        </Box>
        <Box sx={styles.stepContainer_section}>
          <Language
            languageType={preselected?.languageType}
            handleTypeChange={handleLanguageTypeChange}
          />
        </Box>
        <Grid container size={12} sx={{
          justifyContent: 'space-between'
        }}>
          <Button
            variant='contained'
            color='primary'
            sx={styles.button}
            aria-label='back'
            title='back'
            onClick={() => courseDispatch({
              type: CourseActionType.StepChanged,
              activeStep: ProvisionCourseStep.Access,
            })}
          >
            Back
          </Button>
          <Button
            variant='contained'
            color='primary'
            sx={styles.button}
            aria-label='review changes'
            title='review changes'
            onClick={() => courseDispatch({
              type: CourseActionType.DeliveryStepCompleted,
              deliverySelections: {
                frequencyType,
                frequencyPayload,
                availabilityType,
                availabilityPayload,
                languageType,
                ignoreSkipDates
              },
              deliveryChanged: isDeliveryChanged()
            })}
            disabled={!isValid()}
          >
            Review Changes
          </Button>
        </Grid>
      </Box>
    );
}
